// Display · EntityChip
//
// Leaf primitive: tone-soft entity reference chip (variant="chip") or
// inline link-like text (variant="inline"). Uses Box/TextValue primitives
// for all chrome; typography flows through TextValue.
//
// When `href` is set the chip root renders as a real `<a>` element so that
// ctrl/cmd+click and middle-click open the entity in a new browser tab via
// native browser behaviour. Plain left-click is still intercepted by the
// consumer's `onClick` (which calls `e.preventDefault()` to stay SPA).

import { type MouseEvent, type ReactNode } from "react";
import { type ToneName } from "@ds/tokens/palette";
import { TextValue } from "@ds/display/TextValue";
import { Inline } from "@ds/layout/Inline";
import { Box } from "@ds/layout/Box";

// Inline-variant hover CSS (injected once)
const INLINE_CSS_ID = "__ds-entity-chip-inline";
function ensureInlineCSS(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(INLINE_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = INLINE_CSS_ID;
  el.textContent = `.ds-entity-chip-inline:hover { text-decoration: underline; }`;
  document.head.appendChild(el);
}

export interface EntityChipInput {
  label: ReactNode;
  /**
   * Visual variant. Default `"inline"` — bare tinted link text suitable
   * for KV grids, dense tables, and prose. Opt in to `"chip"` explicitly
   * only when the consumer truly needs the chrome (currently: PartyCard).
   */
  variant?: "chip" | "inline";
  icon?: ReactNode;
  countryCode?: string;
  tone?: ToneName;
  arrow?: boolean;
  size?: "sm" | "md";
  testId?: string;
  /**
   * When set, renders the chip root as a real `<a href>` element so that
   * ctrl/cmd+click and middle-click open the entity in a new browser tab.
   * Plain left-click should still be intercepted by `onClick` via
   * `e.preventDefault()` to stay within the SPA router.
   */
  href?: string;
}

export interface EntityChipOutput {
  onClick?: (e: MouseEvent) => void;
}

export type EntityChipProps = EntityChipInput & EntityChipOutput;

export function EntityChip({
  label,
  variant = "inline",
  icon,
  countryCode,
  tone = "blue",
  arrow = true,
  // `size` retained on the public API but no longer drives padding —
  // Box paddingX/Y owns the chrome scale.
  size: _size = "md",
  testId,
  href,
  onClick,
}: EntityChipProps) {
  // ── inline variant — no chrome, just tinted link-like text ────────────
  if (variant === "inline") {
    ensureInlineCSS();

    // When href is set, render as a real anchor via TextValue as="a".
    // TextValue already suppresses browser default text-decoration on <a>
    // and applies the tone color — no raw <a style> needed.
    const as = href ? "a" : "span";

    return (
      <TextValue
        as={as}
        href={href}
        testId={testId}
        className="ds-entity-chip-inline"
        tone={tone}
        weight="medium"
        onClick={onClick}
      >
        {label}
        {arrow && (
          <TextValue as="span" size="xs" tone="grey">
            {" →"}
          </TextValue>
        )}
      </TextValue>
    );
  }

  // ── chip variant ─────────────────────────────────────────────────────
  // When href is set, render the Box root as a real anchor via Box as="a".
  // Box already suppresses browser default text-decoration on <a> and applies
  // tone bg/border/color — no raw <a style> needed.
  const boxAs = href ? "a" : "span";

  return (
    <Box
      as={boxAs}
      href={href}
      tone={tone}
      border
      shape="rounded-lg"
      paddingX="sm"
      paddingY="xs"
      testId={testId}
      onClick={onClick}
    >
      <Inline as="span" inline gap="xs" align="center">
        {icon}
        <TextValue as="span" weight="semibold" size="sm" truncate>
          {label}
        </TextValue>
        {countryCode && (
          <Box
            as="span"
            tone="grey"
            border
            shape="rounded-sm"
            paddingX="xs"
          >
            <TextValue as="span" size="xs" weight="bold" tone="muted" uppercase>
              {countryCode}
            </TextValue>
          </Box>
        )}
        {arrow && (
          <TextValue as="span" size="md" tone="faint">
            {"→"}
          </TextValue>
        )}
      </Inline>
    </Box>
  );
}
